import { Trans, useTranslation } from "@onefront/react-sdk";
import { Field } from "@onefront/react-workspace-components";
import { AccountingConfiguration, ExtendedToggle, Infobox } from "@onefront/workspace-forms";
import { Box, FormHelperText, Stack, useTheme } from "@vapor/react-material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBrowserStorage } from "../../../../hooks/useBrowserStorage";
import WorkspaceDetailsSectionTitle from "../../../general/section-title";
import { BusinessFooterForm } from "../footer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/pro-solid-svg-icons";

export const BusinessAccountingForm = ({
    asset,
    atecoCode,
    selfEmployed,
    accountingConfiguration,
    handleAssets,
    handleAccounting,
    onSave,
    onCancel,
    isEditing,
    isNewBusiness,
    loading
}) => {
    const { t } = useTranslation();

    const {
        palette: { primary }
    } = useTheme();

    const [displayErrors, setDisplayErrors] = useState(false);
    const [errors, setErrors] = useState({
        assetGroup: true,
        assetSpecies: true,
        accounting: true
    });

    const { get: assetList } = useBrowserStorage("assets");
    const { get: atecosList } = useBrowserStorage("ws-utilities::ateco2007");

    const validate = useCallback(body => {
        const newErrors = {
            assetGroup: false,
            assetSpecies: false,
            accounting: false
        };

        if (!body.asset.assetGroup) {
            newErrors.assetGroup = true;
        }

        if (!body.asset.assetSpecies) {
            newErrors.assetSpecies = true;
        }

        if (!body.accountingConfiguration.accountingType) {
            newErrors.accounting = true;
        }

        if (
            body.accountingConfiguration.accountingType === "SIMPLIFIED" &&
            !body.accountingConfiguration.simplifiedAccountingType
        ) {
            newErrors.accounting = true;
        }

        setErrors(newErrors);
    }, []);

    useEffect(() => {
        validate({
            asset: asset,
            accountingConfiguration: accountingConfiguration
        });
    }, [asset, accountingConfiguration, validate]);

    const flatTaxCoefficient = useMemo(() => {
        return atecosList()?.find(item => item.code === atecoCode)?.profitabilityRate;
    }, [atecoCode, atecosList]);

    return (
        <Stack spacing={6} sx={{ width: "740px" }} mb={8}>
            <Box>
                <AccountingConfiguration
                    editing={isEditing}
                    body={accountingConfiguration}
                    onChange={data => {
                        handleAccounting?.(data);

                        if (data.accountingType === "ORDINARY") {
                            handleAssets?.(true, "statutoryDepreciation");
                        } else {
                            handleAssets?.(false, "statutoryDepreciation");
                        }
                    }}
                    loading={loading}
                    selfEmployed={selfEmployed}
                    flatTaxCoefficient={flatTaxCoefficient}
                />

                {displayErrors && errors.accounting && (
                    <FormHelperText error={displayErrors && errors.accounting}>
                        {t("business.error.accountingType")}
                    </FormHelperText>
                )}
            </Box>
            <Stack spacing={4}>
                <WorkspaceDetailsSectionTitle
                    title={t(`business.asset.title`)}
                    subtitle={t(`business.asset.subtitle`)}
                />
                <Stack spacing={isEditing ? 4 : 0}>
                    <Box>
                        <Field
                            component="select"
                            name="select-assets-group"
                            label={t("business.fields.assetGroup")}
                            isEditing={isEditing}
                            value={asset.assetGroup}
                            options={assetList()?.map(asset => ({
                                id: asset.details.group,
                                value: asset.details.description
                            }))}
                            onChange={val => {
                                handleAssets(val, "assetGroup");
                            }}
                            selectHeight="320px"
                            fullWidth
                            loading={loading}
                        />
                        {displayErrors && errors.assetGroup && (
                            <FormHelperText error={displayErrors && errors.assetGroup}>
                                {t("business.error.assetGroup")}
                            </FormHelperText>
                        )}
                    </Box>
                    <Box>
                        <Field
                            component="select"
                            name="select-assets-species"
                            label={t("business.fields.assetSpecies")}
                            isEditing={isEditing}
                            disabled={asset.assetGroup === "" || asset.assetGroup === null}
                            value={asset.assetSpecies}
                            fullWidth
                            options={
                                assetList()
                                    ?.find(item => item.details.group === asset.assetGroup)
                                    ?.details?.kinds?.map(item => ({
                                        id: item.kind,
                                        value: item.description
                                    })) || []
                            }
                            onChange={val => {
                                handleAssets(val, "assetSpecies");
                            }}
                            selectHeight="320px"
                            loading={loading}
                        />
                        {displayErrors && errors.assetSpecies && (
                            <FormHelperText error={displayErrors && errors.assetSpecies}>
                                {t("business.error.assetSpecies")}
                            </FormHelperText>
                        )}
                    </Box>
                    {accountingConfiguration?.accountingType === "ORDINARY" && (
                        <Box mt={isEditing ? 6 : 2}>
                            {isEditing && (
                                <Infobox
                                    title={t("details.business.asset.statutoryDepreciation.infobox.label")}
                                    description={t("details.business.asset.statutoryDepreciation.infobox.description")}
                                    loading={loading}
                                    loadingRows={1}
                                    sx={{ mb: 0 }}
                                />
                            )}
                            <ExtendedToggle
                                name="statutoryDepreciation"
                                value={asset?.statutoryDepreciation}
                                label={!isEditing && t("details.business.asset.statutoryDepreciation.label")}
                                noValueLabel={
                                    <Stack direction={"row"} alignItems={"flex-start"} spacing={1}>
                                        <Box>
                                            <Trans
                                                i18nKey={`details.business.asset.statutoryDepreciation.${
                                                    asset?.statutoryDepreciation ? "activated" : "deactivated"
                                                }`}
                                            />
                                        </Box>
                                        <Box>
                                            <FontAwesomeIcon
                                                icon={asset?.statutoryDepreciation ? faCircleCheck : faCircleXmark}
                                                color={
                                                    asset?.statutoryDepreciation
                                                        ? primary.successed
                                                        : primary.textSubduedColor
                                                }
                                            />
                                        </Box>
                                    </Stack>
                                }
                                editing={isEditing}
                                loading={loading}
                                toggleLabel={{
                                    label: t("details.business.asset.statutoryDepreciation.toggleLabel"),
                                    placement: "end"
                                }}
                                onChange={(event: any) => {
                                    const { name, checked } = event.target;
                                    handleAssets(checked, name);
                                }}
                            />
                        </Box>
                    )}
                </Stack>
            </Stack>
            {isEditing && (
                <BusinessFooterForm
                    isNewBusiness={isNewBusiness}
                    onBack={onCancel}
                    onForward={() => {
                        if (Object.keys(errors).some(key => errors[key])) {
                            setDisplayErrors(true);
                        } else {
                            onSave();
                        }
                    }}
                    loading={loading}
                    disabledForward={loading}
                />
            )}
        </Stack>
    );
};
